import { useCallback, useReducer } from 'react'
import { useIntl } from 'react-intl'
import { useDispatch } from 'react-redux'
import debounce from 'lodash.debounce'
import { mergeAll, isEmpty, reject, equals } from 'ramda'
import { useFlag } from '@unleash/proxy-client-react'

import effects from 'app/effects/effects'
import { generateErrorKeys } from 'helpers/error.helper'
import { networkErrorDetails, networkErrorToMessage } from 'commons/error'

import MessageAlert from 'app/components/MessageAlert'
import { PLACE_INVITATION_DECLINED } from 'app/configs/statusConfigs'
import { isEmptyOrNil } from 'libs/empty'
import { SOURCE_FILTER, filterSource } from 'app/constants/subscriberCRM'
import { useAppName } from 'commons/usePartner'

export const ActionType = {
  SET_LOADER_SUBSCRIBERS: 'SET_LOADER_SUBSCRIBERS',
  SET_EMPTY_SUBSCRIBERS: 'SET_EMPTY_SUBSCRIBERS',
  SET_SUBSCRIBERS: 'SET_SUBSCRIBERS',
  SET_SUBSCRIBERS_NOT_IN_TAG: 'SET_SUBSCRIBERS_NOT_IN_TAG',
  SET_DELETE_LOADING: 'SET_DELETE_LOADING',
  SET_INVITE_LOADING: 'SET_INVITE_LOADING',
  CLEAE_STATE: 'CLEAE_STATE',
}

const initialState = {
  params: {
    page: 1,
    limit: 10,
  },
  isEmptySubscribers: false,
  isLoadingSubscribers: true,
  subscribers: { data: [], meta: {} },
  isFirstLoad: false,
}
let controller = new AbortController()

const reducer = (state, action) => {
  switch (action.type) {
    case ActionType.SET_LOADER_SUBSCRIBERS:
      return {
        ...state,
        isLoadingSubscribers: action.value,
      }
    case ActionType.SET_EMPTY_SUBSCRIBERS:
      return {
        ...state,
        isEmptySubscribers: true,
        isLoadingSubscribers: false,
      }
    case ActionType.SET_SUBSCRIBERS:
      return {
        ...state,
        subscribers: action.value,
        params: action.params,
        isLoadingSubscribers: false,
        isEmptySubscribers: false,
      }
    case ActionType.SET_SUBSCRIBERS_NOT_IN_TAG:
      return {
        ...state,
        subscribers: action.value,
        params: action.params,
        isLoadingSubscribers: false,
        isEmptySubscribers:
          isEmptyOrNil(action.params.keyword) && action.value.data.length === 0,
      }

    case ActionType.SET_DELETE_LOADING:
      return {
        ...state,
        isLoadingSubscribers: action.value,
      }

    case ActionType.CLEAE_STATE:
      return initialState
    default:
      return state
  }
}

export default function useSubscribers({
  resetSubscriberSelection = () => {},
} = {}) {
  const [state, dispatch] = useReducer(reducer, initialState)
  const { formatMessage } = useIntl()
  const commonDispatch = useDispatch()
  const appName = useAppName()
  const mes_5173_crm_bulk_delete_subscribers = useFlag(
    'mes_5173_crm_bulk_delete_subscribers',
  )

  const invitationMessage = {
    success: formatMessage({
      id: 'subscriber.invite.success',
    }),
    error: formatMessage({
      id: 'subscriber.invite.error',
    }),
  }

  const approveMessage = formatMessage({
    id: 'subscriber.approved.msgText',
  })

  const rejectMessage = formatMessage({
    id: 'subscriber.rejected.msgText',
  })

  const handleAlertError = useCallback(
    (error) => {
      let errorMessage
      const errorDetails = networkErrorDetails(error)
      if (errorDetails?.code === 602) {
        const errors = errorDetails.message.errors
        const errorKeys = generateErrorKeys(errors, formatMessage)

        if (errorKeys.length > 0) {
          errorMessage = (
            <div>
              {errorKeys.map((errorMsg, index) => (
                <p key={index}>{errorMsg}</p>
              ))}
            </div>
          )
        }
      }

      if (!errorMessage) {
        const { message } = networkErrorToMessage(formatMessage)(error)
        errorMessage = message ?? formatMessage({ id: 'error.general' })
      }

      return commonDispatch(
        MessageAlert.showError({
          message: errorMessage,
        }),
      )
    },
    [commonDispatch, formatMessage],
  )

  const fetchSubscribers = useCallback(
    async (params = initialState.params) => {
      controller.abort()
      controller = new AbortController()
      await effects.apis
        .subscribersV2(params, {
          signal: controller.signal,
        })
        .then((response) => {
          if (equals(params, initialState.params) && response.meta.total === 0)
            dispatch({ type: 'SET_EMPTY_SUBSCRIBERS' })
          else
            dispatch({
              type: 'SET_SUBSCRIBERS',
              value: response,
              params: params,
            })
        })
        .catch((error) => {
          if (error?.name !== 'CanceledError') handleAlertError(error)
        })
    },
    [handleAlertError],
  )

  const fetchSubscriberNotInTags = useCallback(
    async (params = initialState.params) => {
      dispatch({ type: 'SET_LOADER_SUBSCRIBERS', value: true })
      controller.abort()
      controller = new AbortController()
      await effects.apis
        .subscriberNotInTags(params, {
          signal: controller.signal,
        })
        .then((response) => {
          dispatch({
            type: 'SET_SUBSCRIBERS_NOT_IN_TAG',
            value: response,
            params: params,
          })
        })
        .catch((error) => {
          if (error?.name !== 'CanceledError') handleAlertError(error)
        })
    },
    [handleAlertError],
  )

  const handleSearchSubscriberForTags = (keyword, onClearData) => {
    const setParams = reject(equals(''))(
      mergeAll([{ ...state.params, keyword: keyword, page: 1, limit: 10 }]),
    )
    debounceFetchSubscriberNotInTags(setParams, onClearData)
  }

  const debounceFetchSubscriberNotInTags = useCallback(
    debounce((value, onClearData) => {
      onClearData()
      dispatch({ type: 'SET_LOADER_SUBSCRIBERS', value: true })
      fetchSubscriberNotInTags(value)
    }, 1000),
    [],
  )

  const handleDeleteSubscriber = async (
    requestData,
    onAction,
    subscriberCount,
  ) => {
    dispatch({ type: ActionType.SET_DELETE_LOADING, value: true })
    try {
      if (mes_5173_crm_bulk_delete_subscribers) {
        await effects.apis.bulkDeleteSubscribers(requestData)
        if (subscriberCount !== undefined) {
          commonDispatch(
            MessageAlert.showSuccess({
              message: formatMessage(
                { id: 'crm.subscribers.deleted' },
                { count: subscriberCount },
              ),
            }),
          )
        }
      } else {
        await effects.apis.deleteSubscribersV2(requestData)
      }
      if (onAction) {
        await onAction()
      }
      resetSubscriberSelection()
    } catch (error) {
      const errorCode = error?.response?.data?.details?.code
      if (errorCode === 403) {
        commonDispatch(
          MessageAlert.showError({
            message: formatMessage({ id: 'error.permission.denied' }),
          }),
        )
      } else {
        handleAlertError(error)
      }
    } finally {
      dispatch({ type: ActionType.SET_DELETE_LOADING, value: false })
    }
  }

  const handleInviteSubscriber = useCallback(
    async (subscriberId, onAction) => {
      try {
        await effects.apis.inviteSubscribers(subscriberId)
        await fetchSubscribers(state.params)
        commonDispatch(
          MessageAlert.showSuccess({ message: invitationMessage.success }),
        )
        if (onAction) {
          onAction()
        }
        resetSubscriberSelection()
      } catch (error) {
        handleAlertError(error)
        commonDispatch(
          MessageAlert.showError({ message: invitationMessage.error }),
        )
      }
    },
    [fetchSubscribers, handleAlertError, state.params],
  )

  const debounceFetchSubscribers = useCallback(
    debounce((value) => {
      dispatch({ type: 'SET_LOADER_SUBSCRIBERS', value: true })
      fetchSubscribers(value)
    }, 1000),
    [],
  )

  const handleSearch = (keyword) => {
    const setParams = reject(equals(''))(
      mergeAll([{ ...state.params, keyword: keyword, page: 1, limit: 10 }]),
    )
    debounceFetchSubscribers(setParams)
    resetSubscriberSelection()
  }

  const handleFetchSubscribers = useCallback(
    async (params) => {
      dispatch({ type: 'SET_LOADER_SUBSCRIBERS', value: true })
      await fetchSubscribers(params)
    },
    [fetchSubscribers],
  )

  const handleGroups = (value = {}) => {
    const result = reject(
      isEmpty,
      mergeAll([
        {
          ...state.params,
          page: 1,
          limit: 10,
          subgroups: Object.values(value.tags).flat(),
        },
      ]),
    )
    resetSubscriberSelection()
    handleFetchSubscribers(result)
  }

  const handleFilter = (selected = [], key) => {
    let checkSelectedFilter = {}

    if (isEmpty(selected)) {
      checkSelectedFilter = mergeAll([
        {
          ...state.params,
          page: 1,
          limit: 10,
        },
      ])
      delete checkSelectedFilter[key]
    } else {
      let selectedValues = selected.map((sel) => sel.value)

      // If source filter and all source options are selected, add '-1' to the array
      if (
        key === SOURCE_FILTER &&
        selected.length === filterSource(appName).length
      ) {
        selectedValues = [...selectedValues, '-1']
      }

      checkSelectedFilter = mergeAll([
        {
          ...state.params,
          page: 1,
          limit: 10,
          [key]: selectedValues.toString(),
        },
      ])
    }
    resetSubscriberSelection()
    handleFetchSubscribers(checkSelectedFilter)
  }

  const handlePendingApproveSubscriber = useCallback(
    async ({ ids, placeId, status }, onAction) => {
      dispatch({ type: ActionType.SET_LOADER_SUBSCRIBERS, value: true })
      try {
        await effects.apis.approveSubscriber({
          placeId,
          ids,
          status,
        })
        if (onAction) {
          await onAction()
        }
        commonDispatch(
          MessageAlert.showSuccess({
            message:
              status === PLACE_INVITATION_DECLINED
                ? rejectMessage
                : approveMessage,
          }),
        )
        resetSubscriberSelection()
      } catch (error) {
        return handleAlertError(error)
      } finally {
        dispatch({ type: ActionType.SET_LOADER_SUBSCRIBERS, value: false })
      }
    },
    [fetchSubscribers, handleAlertError, state.params],
  )

  const handleMarkCorrect = useCallback(
    async ({ subscriberId }, onAction) => {
      dispatch({ type: 'SET_LOADER_SUBSCRIBERS', value: true })
      try {
        await effects.apis.markAsCorrectSubscriber(subscriberId)
        if (onAction) {
          await onAction()
        }
        resetSubscriberSelection()
      } catch (error) {
        handleAlertError(error)
      } finally {
        dispatch({ type: 'SET_LOADER_SUBSCRIBERS', value: false })
      }
    },
    [fetchSubscribers, handleAlertError, state.params],
  )

  const handleResetFilter = () => {
    handleFetchSubscribers({ ...initialState.params })
  }

  const onClearState = () => {
    dispatch({ type: ActionType.CLEAE_STATE })
  }

  return {
    state,
    handleSearch,
    handleGroups,
    handleFilter,
    handleResetFilter,
    handleFetchSubscribers,
    handleDeleteSubscriber,
    handleInviteSubscriber,
    handlePendingApproveSubscriber,
    fetchSubscriberNotInTags,
    handleSearchSubscriberForTags,
    onClearState,
    handleMarkCorrect,
  }
}
